import { createRouter, createWebHistory } from 'vue-router'
import type { RouteRecordRaw } from 'vue-router'
import { useUserStore } from '@/stores/userStore'
import { useSeo, getContentGroup } from '@/composables/useSeo'
import { authReady } from '@/authState'

const routes: RouteRecordRaw[] = [
  {
    path: "/discover",
    name: "discover",
    component: () => import("@/views/Discover.vue")
    // Open to guests - browse public recipes
  },
  {
    path: "/features",
    name: "features",
    component: () => import("@/views/Features.vue")
    // Open to guests - SEO/marketing page
  },
  {
    path: "/",
    name: "landing",
    component: () => import("@/views/LandingPage.vue"),
    meta: {
      requiresGuest: true
    }
  },
  {
    path: "/home",
    name: "home",
    component: () => import("@/views/Home.vue"),
    meta: {
      requiresAuth: true
    }
  },
  {
    path: "/login",
    name: "login",
    component: () => import("@/views/Login.vue"),
    meta: {
      requiresGuest: true
    }
  },
  {
    path: "/meal_planning",
    name: "meal_planning",
    component: () => import("@/views/MealPlanning.vue"),
    meta: {
      requiresAuth: true
    }
  },
  {
    path: "/pantry",
    name: "pantry",
    component: () => import("@/views/Pantry.vue"),
    meta: {
      requiresAuth: true
    }
  },
  {
    path: "/recipe_books",
    name: "recipe_books",
    component: () => import("@/views/RecipeBooks.vue"),
    meta: {
      requiresAuth: true
    }
  },
  {
    path: "/recipe_book/:id",
    name: "recipe_book",
    component: () => import("@/views/RecipeBook.vue"),
    meta: {
      requiresAuth: true
    }
  },
  {
    path: "/recipe/:id",
    name: "recipe",
    component: () => import("@/views/Recipe.vue")
    // meta: {
    //   requiresAuth: true
    // }
  },
  {
    path: "/recipe/:id/cook",
    name: "cook_recipe",
    component: () => import("@/views/CookModePage.vue")
  },
  {
    path: "/recipeBook/:recipeBookId/recipe/:id",
    name: "recipe_book_recipe",
    component: () => import("@/views/Recipe.vue")
    // meta: {
    //   requiresAuth: true
    // }
  },
  {
    path: "/sandbox",
    name: "sandbox",
    component: () => import("@/views/Sandbox.vue")
  },
  {
    path: "/ai-review",
    name: "ai_review",
    component: () => import("@/views/AiReview.vue"),
    meta: {
      requiresAuth: true
    }
  },
  {
    path: "/settings",
    name: "settings",
    component: () => import("@/views/Settings.vue"),
    meta: {
      requiresAuth: true
    }
  },
  {
    path: "/pricing",
    name: "pricing",
    component: () => import("@/views/Pricing.vue")
    // Open to guests - view pricing
  },
  {
    path: "/privacy-policy",
    name: "privacy-policy",
    component: () => import("@/views/PrivacyPolicy.vue")
    // Open to guests - required for app store listings
  },
  {
    path: "/subscription-success",
    name: "subscription-success",
    component: () => import("@/views/SubscriptionSuccess.vue"),
    meta: {
      requiresAuth: true
    }
  },
  {
    path: "/shopping_lists",
    name: "shopping_lists",
    component: () => import("@/views/ShoppingLists.vue"),
    meta: {
      requiresAuth: true
    }
  },
  {
    path: "/join/:inviteCode",
    name: "join_household",
    component: () => import("@/views/JoinHousehold.vue"),
    meta: {
      requiresAuth: true
    }
  },
  {
    path: "/admin",
    name: "admin",
    component: () => import("@/views/Admin.vue"),
    meta: {
      requiresAuth: true,
      requiresCreator: true
    }
  }
]

// Add dev-only routes
if (import.meta.env.DEV) {
  routes.push({
    path: "/data-migration",
    name: "data_migration",
    component: () => import("@/views/DataMigration.vue"),
    meta: {
      requiresAuth: true
    }
  })

}

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  scrollBehavior(to, from, savedPosition) {
    // Ensure navigating to a recipe (or any route) lands at the top.
    // Preserve browser back/forward saved position when available.
    if (savedPosition) return savedPosition
    return { top: 0 }
  }
})

router.beforeEach(async (to, from, next) => {
  console.log("Router guard: Navigation starting")

  const requiresAuth = to.matched.some((record) => record.meta.requiresAuth)
  const requiresGuest = to.matched.some((record) => record.meta.requiresGuest)
  const requiresCreator = to.matched.some((record) => record.meta.requiresCreator)

  // Get the store instance
  const userStore = useUserStore()

  // Firebase restores persisted sessions asynchronously. Wait for the initial
  // auth callback instead of guessing with a timing delay.
  await authReady

  const isLoggedIn = userStore.isLoggedIn
  const user = userStore.getUser

  console.log("Router guard: Auth check", {
    isLoggedIn,
    hasUser: !!user,
    userId: user?.uid,
    requiresAuth,
    requiresGuest,
    toPath: to.path
  })

  let route: string | null = null

  if (isLoggedIn) {
    if (requiresCreator && userStore.membershipTier !== 'creator') {
      console.warn("🚫 Creator access required - redirecting to home")
      route = "/home"
    } else if (requiresAuth) {
      console.log("✅ isLoggedIn && requiresAuth - proceeding")
    } else if (requiresGuest) {
      console.warn("🔄 isLoggedIn && requiresGuest - redirecting to home")
      route = "/home"
    } else {
      console.log("✅ isLoggedIn && no meta requirements - proceeding")
    }
  } else {
    if (requiresAuth) {
      console.warn("🚫 NOT isLoggedIn && requiresAuth - redirecting to login")
      route = "/login?signInSuccessUrl=" + encodeURIComponent(to.fullPath)
    } else if (requiresGuest) {
      console.log("✅ NOT isLoggedIn && requiresGuest - proceeding")
    } else {
      console.warn("NOT isLoggedIn && no meta requirements")
    }
  }

  if (route !== null) {
    next(route)
  } else {
    next()
  }
})

// Track page views in GA4 after navigation
router.afterEach((to) => {
  // Use nextTick to ensure the page title has been updated
  setTimeout(() => {
    const { trackPageView } = useSeo()
    const userStore = useUserStore()

    trackPageView({
      page_title: document.title,
      page_location: window.location.href,
      content_group: getContentGroup(to.path),
      user_tier: userStore.isPremium ? 'premium' : 'free',
      logged_in: userStore.isLoggedIn
    })
  }, 100)
})

export default router
